import { useEffect, useState } from "react";
import { FileText, Table2 } from "lucide-react";
import { fetchSourceSnippet } from "../../api/client";
import { prettyFileType } from "../../utils/labels";
import ExcelSourceView from "./ExcelSourceView";
import PdfSourceView from "./PdfSourceView";

interface SourceData {
  snippet: string | null;
  source_type: string;
  sheet_name: string | null;
  cell_address: string | null;
  page_number: number | null;
  column_label: string | null;
  bounding_box: number[] | null;
}

interface SourceViewerProps {
  dealId: string;
  docId: string;
  filename: string;
  fileType: string;
  activeFieldPath: string | null;
}

export default function SourceViewer({
  dealId,
  docId,
  filename,
  fileType,
  activeFieldPath,
}: SourceViewerProps) {
  const [source, setSource] = useState<SourceData | null>(null);
  const [loading, setLoading] = useState(false);

  const ext = filename.split(".").pop()?.toLowerCase() || "";
  const isExcel = ["xlsx", "xls"].includes(ext);
  const isPdf = ext === "pdf";

  useEffect(() => {
    if (!activeFieldPath) {
      setSource(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchSourceSnippet(dealId, docId, activeFieldPath)
      .then((res) => {
        if (!cancelled) {
          setSource({
            snippet: res.snippet,
            source_type: (res as any).source_type || "unknown",
            sheet_name: (res as any).sheet_name || null,
            cell_address: (res as any).cell_address || null,
            page_number: (res as any).page_number || null,
            column_label: (res as any).column_label || null,
            bounding_box: (res as any).bounding_box || null,
          });
          setLoading(false);
        }
      })
      .catch(() => { if (!cancelled) { setSource(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [dealId, docId, activeFieldPath]);

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border border-grove-300 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-grove-200 bg-grove-50 shrink-0">
        {isExcel ? <Table2 className="w-4 h-4 text-forest-600" /> : <FileText className="w-4 h-4 text-forest-600" />}
        <span className="text-sm font-medium text-bark-700 truncate">{filename}</span>
        <span className="ml-auto text-xs text-bark-400 uppercase">{prettyFileType(fileType)}</span>
      </div>

      {/* Document content fills the pane. The badge strip and "AI Inferred"
          card were removed per user request — the source viewer just shows
          the document with the relevant cell/region highlighted when known,
          and shows the document unchanged when the source is unknown. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {isExcel && (
          <ExcelSourceView
            dealId={dealId}
            docId={docId}
            activeCell={source?.cell_address || null}
            sheetName={source?.sheet_name || null}
          />
        )}

        {!isExcel && isPdf && (
          <PdfSourceView
            dealId={dealId}
            docId={docId}
            activePage={source?.page_number || null}
            activeBbox={source?.bounding_box || null}
          />
        )}

        {!isExcel && !isPdf && (
          <div className="p-4 space-y-3 overflow-y-auto h-full">
            {!activeFieldPath && (
              <div className="flex flex-col items-center justify-center h-full text-bark-400">
                <FileText className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">Preview not available for this file type</p>
              </div>
            )}
            {activeFieldPath && loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-forest-600 border-t-transparent" />
              </div>
            )}
            {activeFieldPath && !loading && source?.snippet && (
              <div className="px-4 py-3 bg-honey-50 border border-honey-200 rounded-lg animate-pulse-once">
                <p className="text-sm font-mono text-bark-800 whitespace-pre-wrap break-words">
                  {source.snippet}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
